import { supabase } from '@/lib/supabase';

// UGC Marketplace service: deals, bids, submissions, invoices
export const ugcMarketplaceService = {
  // Deals
  async listOpenDeals() {
    const { data, error } = await supabase
      .from('ugc_deals')
      .select('*')
      .eq('status', 'open')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async listMyDeals(brandUserId) {
    const { data, error } = await supabase
      .from('ugc_deals')
      .select('*')
      .eq('brand_user_id', brandUserId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async createDeal(deal) {
    const payload = {
      user_id: deal.brandUserId,  // Legacy column - required NOT NULL
      brand_user_id: deal.brandUserId,
      brand: deal.title || 'Untitled Deal',  // Legacy column - required NOT NULL
      title: deal.title,
      brief: deal.brief,
      deliverables: deal.deliverables,
      budget_min: deal.budgetMin || null,
      budget_max: deal.budgetMax || null,
      deadline: deal.deadline || null,
      status: 'open'
    };
    console.log('UGC Service: Creating deal with payload:', payload);
    const { data, error } = await supabase
      .from('ugc_deals')
      .insert(payload)
      .select()
      .single();
    if (error) {
      console.error('UGC Service: Insert failed:', error);
      throw error;
    }
    console.log('UGC Service: Deal created:', data);
    return data;
  },

  async closeDeal(dealId, status = 'closed') {
    const { data, error } = await supabase
      .from('ugc_deals')
      .update({ status })
      .eq('id', dealId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Bids
  async createBid(dealId, creatorUserId, { bidAmount, bidNote, portfolioUrl }) {
    const payload = {
      deal_id: dealId,
      creator_user_id: creatorUserId,
      bid_amount: bidAmount,
      bid_note: bidNote || '',
      portfolio_url: portfolioUrl || null,
      status: 'pending'
    };
    const { data, error } = await supabase
      .from('ugc_deal_bids')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async listBidsForDeal(dealId) {
    const { data, error } = await supabase
      .from('ugc_deal_bids')
      .select('*')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async listMyBids(creatorUserId) {
    const { data, error } = await supabase
      .from('ugc_deal_bids')
      .select('*, deal:ugc_deals(*)')
      .eq('creator_user_id', creatorUserId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async acceptBid(dealId, bidId, { brandUserId, creatorUserId, bidAmount } = {}) {
    const { data, error } = await supabase
      .from('ugc_deal_bids')
      .update({ status: 'accepted' })
      .eq('id', bidId)
      .eq('deal_id', dealId)
      .select()
      .single();
    if (error) throw error;

    // Mark deal as awarded
    await supabase.from('ugc_deals').update({ status: 'awarded' }).eq('id', dealId);

    // Create CRM entry for creator (if brandUserId provided)
    if (brandUserId && creatorUserId) {
      try {
        // Get creator info from auth
        const { data: creatorAuth } = await supabase.auth.admin.getUserById(creatorUserId);
        if (creatorAuth?.user?.email) {
          const { crmContactService } = await import('./crmContactService.js');
          await crmContactService.createContact({
            name: creatorAuth.user.user_metadata?.full_name || 'Creator',
            email: creatorAuth.user.email,
            phone: null,
            notes: `Bid accepted on deal ${dealId}. Amount: $${bidAmount}`,
            tags: ['marketplace', 'accepted-bid'],
            brandId: brandUserId
          });
        }
      } catch (err) {
        console.error('CRM entry creation failed:', err);
        // Don't fail the bid accept if CRM fails
      }
    }

    return data;
  },

  async rejectBid(bidId) {
    const { data, error } = await supabase
      .from('ugc_deal_bids')
      .update({ status: 'rejected' })
      .eq('id', bidId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Submissions
  async submitDelivery(dealId, bidId, creatorUserId, { storagePath, notes }) {
    const payload = {
      deal_id: dealId,
      bid_id: bidId,
      creator_user_id: creatorUserId,
      storage_path: storagePath,
      notes: notes || '',
      status: 'submitted'
    };
    const { data, error } = await supabase
      .from('ugc_deal_submissions')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async listSubmissionsForDeal(dealId) {
    const { data, error } = await supabase
      .from('ugc_deal_submissions')
      .select('*')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // Invoices
  async createInvoiceFromBid({ dealId, brandUserId, creatorUserId, amount, dueDate, payload }) {
    const record = {
      deal_id: dealId,
      brand_user_id: brandUserId,
      creator_user_id: creatorUserId,
      amount,
      due_date: dueDate || null,
      status: 'draft',
      payload: payload || null
    };
    const { data, error } = await supabase
      .from('ugc_invoices')
      .insert(record)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateInvoiceStatus(invoiceId, status) {
    const { data, error } = await supabase
      .from('ugc_invoices')
      .update({ status })
      .eq('id', invoiceId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async listMyInvoices(userId) {
    const { data, error } = await supabase
      .from('ugc_invoices')
      .select('*')
      .or(`creator_user_id.eq.${userId},brand_user_id.eq.${userId}`)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  }
};

export default ugcMarketplaceService;
